import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { Dispatch, FC, FocusEvent, SetStateAction, useEffect } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ENoticeType } from 'src/enums';
import { TFileString } from 'src/global';
import { isObjectEmpty } from 'src/helpers';
import { setApplicationFormChanged } from 'src/redux/appSlice';
import { setNotice } from 'src/redux/noticesSlice';
import { IClientFull, useUpdateClientMutation } from 'src/redux/services/clients';
import * as yup from 'yup';
import { CLIENT_ID } from '../../../constants';
import { ReactComponent as AppIcon } from '../../../icons/App.svg';
import { IProvider } from '../../../redux/services/provider';
import {
  IProviderRule,
  useGetCatalogEnabledQuery,
  useGetClientTypesQuery,
} from '../../../redux/services/settings';
import { CustomSelect } from '../../custom/CustomSelect';
import { CustomTypography } from '../../custom/CustomTypography';
import { SettingsItem } from '../SettingsItem';
import styles from './ClientSettings.module.css';
import { DescriptionField } from './ClientSettingsFields';
import { UploadAndDisplayImage } from './UploadAndDisplayImage';
import { UploadCroppedImage } from './UploadCroppedImage';

interface IClientSettingsHeaderProps {
  client: IClientFull;
  coverSrc: string | null;
  setCoverSrc: Dispatch<SetStateAction<string | null>>;
}

export interface TProviderItem {
  rule: IProviderRule;
  provider: IProvider;
}

export const ClientSettingsHeader: FC<IClientSettingsHeaderProps> = ({
  client,
  coverSrc,
  setCoverSrc,
}) => {
  const dispatch = useDispatch();
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const [updateClient, updateClientResult] = useUpdateClientMutation();
  const { data: types = [] } = useGetClientTypesQuery();
  const { data: catalogEnabled } = useGetCatalogEnabledQuery();

  type TClientSettingsInputs = {
    name: string;
    description: string;
    group: string;
    type_id: string | null;
    is_visible: boolean;
    avatar: TFileString;
    cover: TFileString;
  };

  const schema = yup
    .object({
      name: yup
        .string()
        .max(50, 'Название не может превышать 50 символов')
        .required('Обязательное поле'),
      description: yup.string().max(255, 'Описание не может превышать 255 символов').nullable(),
    })
    .required();

  const methods = useForm<TClientSettingsInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: client.name,
      description: client.description,
      type_id: client?.type?.id || 'empty',
      avatar: client.avatar,
      cover: client.cover,
      is_visible: client.is_visible || false,
    },
    mode: 'onChange',
  });

  const {
    register,
    control,
    clearErrors,
    setValue,
    setError,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  useEffect(() => {
    return () => {
      dispatch(setApplicationFormChanged(false));
    };
  }, []);

  useEffect(() => {
    if (updateClientResult.isSuccess) {
      dispatch(
        setNotice({
          type: ENoticeType.info,
          id: Math.random(),
          isRead: false,
          message: `Основная информация обновлена`,
          timestamp: new Date().toString(),
        }),
      );
    }
  }, [updateClientResult]);

  const onSubmit: SubmitHandler<TClientSettingsInputs> = async (data) => {
    try {
      if (Object.keys(errors).length) return;
      const payload = (Object.keys(dirtyFields) as Array<keyof typeof data>).reduce(
        (acc, field) => ({ ...acc, [field]: data[field] }),
        {} as Partial<typeof data>,
      );
      await updateClient({
        id: client?.client_id,
        data: payload,
      }).unwrap();
      reset(data);
    } catch (e) {
      console.log('err', e);
    }
  };

  const setCoverValue = (value: File | null) => setValue('cover', value, { shouldDirty: true });
  const setCoverError = (error: string) => setError('cover', { message: error });
  const clearCoverError = () => clearErrors('cover');
  const isAdminClient = client.client_id === CLIENT_ID;

  return (
    <>
      <CustomTypography className={clsx('font-golos', 'text-24-medium', styles.title)}>
        Настройки приложения {client.name}
      </CustomTypography>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsItem title="Основная информация">
            <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
              Название приложения
            </CustomTypography>
            <TextField
              {...register('name', {
                onBlur: (event: FocusEvent<HTMLInputElement>) => {
                  setValue('name', event.target.value.trim());
                },
              })}
              className="custom"
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : ''}
              fullWidth
              variant="standard"
            />
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              Имя приложения, отображаемое пользователям
            </CustomTypography>

            {!isAdminClient && (
              <>
                <CustomTypography className={clsx('text-14', styles['input-title'])}>
                  Описание приложения
                </CustomTypography>
                <TextField
                  {...register('description')}
                  className="custom"
                  error={!!errors.description}
                  helperText={errors.description ? errors.description.message : ''}
                  fullWidth
                  variant="standard"
                  multiline
                />
                <DescriptionField control={control} />
              </>
            )}
            <div>
              <CustomTypography className={clsx('text-14', styles['input-title'])}>
                Логотип приложения
              </CustomTypography>
              <UploadAndDisplayImage
                componentName={'logo-image'}
                defaultIcon={
                  appId === CLIENT_ID && !clientId ? (
                    'public/default/logo.png'
                  ) : (
                    <AppIcon fill="#ced0d9" />
                  )
                }
              />
              {errors.avatar && (
                <CustomTypography color="error" className={clsx('text-14', styles['input-error'])}>
                  {errors.avatar.message}
                </CustomTypography>
              )}
              <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
                Файл формата JPG, PNG, BMP или WEBP. Максимальный размер - 1 МБ.
              </CustomTypography>
              <CustomTypography className={clsx('text-14', styles['input-title'])}>
                Обложка приложения
              </CustomTypography>
              <UploadCroppedImage
                error={errors.cover?.message}
                maxImageSize={5}
                setAvatarError={setCoverError}
                imgSrc={coverSrc}
                setImgSrc={setCoverSrc}
                clearAvatarError={clearCoverError}
                defaultValue={client?.cover || null}
                componentName={'cover-image'}
                setAvatarValue={setCoverValue}
                pathToAvatar={null}
              />
              {errors.cover && (
                <CustomTypography color="error" className={clsx('text-14', styles['input-error'])}>
                  {errors.cover.message}
                </CustomTypography>
              )}
              <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
                Файл с расширением .jpg, .jpeg, .png. Максимальный размер - 5 МБ.
              </CustomTypography>
            </div>
            <div className={styles['submit-buttons']}>
              <Button
                className={styles['create-button']}
                type="submit"
                variant="custom"
                disabled={isObjectEmpty(dirtyFields)}
              >
                Сохранить
              </Button>
            </div>
          </SettingsItem>

          {!isAdminClient && catalogEnabled && (
            <SettingsItem title="Каталог">
              <CustomTypography className={clsx('text-14', styles['input-title'])}>
                Тип приложения
              </CustomTypography>
              <Controller
                control={control}
                name="type_id"
                render={({ field }) => (
                  <CustomSelect
                    ref={null}
                    className={styles.select}
                    defaultValue={client?.type?.id ?? ''}
                    data-id="group-select"
                    onChange={(e) => {
                      field.onChange(e.target.value === 'empty' ? '' : e.target.value);
                    }}
                    value={field.value === '' ? 'empty' : field.value}
                  >
                    <MenuItem
                      className="custom-select"
                      key={-1}
                      value={'empty'}
                      data-id={`group-item-empty`}
                    >
                      Прочее
                    </MenuItem>
                    {types.map((item, index) => (
                      <MenuItem
                        className="custom-select"
                        key={index}
                        value={item.id}
                        data-id={`group-item-${item.id}`}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                )}
              />
              <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
                Значение задаваемое из списка типов
              </CustomTypography>

              <div className={styles['switch-wrapper']}>
                <CustomTypography className={clsx('text-14')}>
                  Отображать в каталоге
                </CustomTypography>
                <Switch defaultChecked={client?.is_visible || false} {...register('is_visible')} />
              </div>

              <div className={styles['submit-buttons']}>
                <Button
                  className={styles['create-button']}
                  type="submit"
                  variant="custom"
                  disabled={isObjectEmpty(dirtyFields)}
                >
                  Сохранить
                </Button>
              </div>
            </SettingsItem>
          )}
        </form>
      </FormProvider>
    </>
  );
};
